import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { inviteTokenForModel } from '@trello/invitation-tokens';
import { Spinner } from '@trello/nachos/spinner';
import { getOrganizationHomeUrl } from '@trello/urls';

import { simpleDomainRestriction } from './HelperFunctions';
import { useInvitationTokenPageQuery } from './InvitationTokenPageQuery.generated';
import { NotLoggedInUserText } from './NotLoggedInUserText';

import styles from './InvitationTokenPage.less';

interface InvitationTokenPageProps {
  orgId: string;
}

const InviteRestrictValues = ['any', 'managed', 'managed-or-domain', 'domain'];

export const InvitationTokenPage: FunctionComponent<
  InvitationTokenPageProps
> = ({ orgId }) => {
  const { data, loading } = useInvitationTokenPageQuery({
    variables: { orgId },
    waitOn: ['MemberHeader'],
  });

  if (loading || !data?.organization) {
    return <Spinner centered />;
  }

  const { organization } = data;
  const inviteToken = inviteTokenForModel(organization.id);
  const idMemberInviter = inviteToken?.split('-')[1];
  const inviter = organization.members.find((m) => m.id === idMemberInviter);
  const inviterName = inviter?.fullName;
  const modelNameText = organization.displayName;

  const emailRestrictions = (
    organization?.prefs?.orgInviteRestrict ?? []
  )?.filter((value) => !Object.values(InviteRestrictValues).includes(value));

  const requiredDomain =
    emailRestrictions.length === 1
      ? simpleDomainRestriction(emailRestrictions[0])
      : null;

  return (
    <div className={styles.invitationLink}>
      <div className={styles.description}>
        <FormattedMessage
          id="templates.invitation_link.youve-been-invited-safe"
          defaultMessage="{inviterName} invited you to {name}!"
          description="Page headline: {Name of inviting member} invited you to {name of workspace}"
          values={{
            inviterName: <strong>{inviterName}</strong>,
            name: <strong>{modelNameText}</strong>,
          }}
        />
      </div>
      <NotLoggedInUserText
        requiredDomain={requiredDomain}
        emailRestrictions={emailRestrictions}
        returnUrl={getOrganizationHomeUrl(organization.name)}
        modelName="Team"
        analyticsContainers={{ workspace: { id: orgId } }}
        memberString=""
        memberships={[]}
      />
    </div>
  );
};
